import Anchor from '@/components/ui/Anchor'
import Icon from '@/components/ui/Icon'
import { useData } from '@/hooks/useLocale'

// Western digits with thousands separators in both languages, as the app shows them.
const number = new Intl.NumberFormat('en-US')

// A chip's share of the 6-column row, as a class.
const SPAN = { 2: 'col-span-2', 3: 'col-span-3', 6: 'col-span-6' }

/** Feature chips fill a 6-column grid in rows of 2, 1, 3, 2, 1, 3… chips. */
function chipSpans(count) {
  const spans = []
  for (let row = 0; spans.length < count; row++) {
    const n = Math.min([2, 1, 3][row % 3], count - spans.length)
    spans.push(...Array(n).fill(6 / n))
  }
  return spans
}

/** One plan at the chosen billing period; the `featured` plan is blue. */
export default function PricingCard({ plan, billing }) {
  const { PRICING } = useData()
  const hl = plan.featured
  const spans = chipSpans(plan.features.length)
  const price = plan.price[billing.id]

  return (
    <div
      className={`flex min-w-0 flex-col gap-[18px] rounded-3xl p-6 ${hl ? 'bg-brand text-white shadow-[0_30px_60px_-30px_rgba(26,115,232,.55)]' : 'bg-white text-[#1e2330]'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-semibold">{plan.name}</span>
        <span
          className={`grid size-9 shrink-0 place-items-center rounded-xl ${hl ? 'bg-white/18 text-white' : 'bg-brand-tint text-brand'}`}
        >
          <Icon name={plan.icon} size={18} />
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[clamp(32px,3.4vw,48px)] leading-none tracking-[-0.035em] whitespace-nowrap tabular-nums rtl:leading-[1.4]">
          {price ? number.format(price) : PRICING.free}
        </span>
        <span className={`text-[13px] ${hl ? 'text-[#d2e3fc]' : 'text-subtle'}`}>
          {`${price ? billing.unit : plan.freeNote} · ${plan.users}`}
        </span>
      </div>

      <span className={`text-sm/normal ${hl ? 'text-[#e3edfd]' : 'text-muted'}`}>
        {plan.description}
      </span>

      <ul className="grid grid-cols-6 gap-1.5">
        {plan.features.map((feature, i) => (
          <li
            key={feature}
            className={`${SPAN[spans[i]]} flex h-8 min-w-0 items-center justify-center overflow-hidden rounded-2xl px-2.5 text-xs font-medium whitespace-nowrap ${hl ? 'bg-white/16 text-white' : 'bg-surface text-body'}`}
          >
            {feature}
          </li>
        ))}
      </ul>

      <Anchor
        href={plan.cta.href}
        className={`mt-auto flex h-11 items-center justify-between rounded-xl ps-4 pe-2 text-sm font-semibold ${hl ? 'bg-white text-brand hover:bg-brand-tint hover:text-brand-dark' : 'bg-brand text-white hover:bg-brand-dark'}`}
      >
        {plan.cta.label}
        <span
          className={`grid size-[30px] place-items-center rounded-[9px] ${hl ? 'bg-brand-tint' : 'bg-white/14'}`}
        >
          <Icon name="arrowRight" size={14} stroke={2.4} />
        </span>
      </Anchor>
    </div>
  )
}
